import { createRouter, createWebHistory } from 'vue-router'
import getLanguage from '@/helper/language'
import { getWebTarget } from '@/services/user-management.js'
import { logout, login } from '@/services/keycloak-service'
import Person from '@/views/selfcare/person/index'
import Security from '@/views/selfcare/security/index'
import { useUserStore } from '@/store/module-user.js'

const APP_BASE = '/benutzerverwaltung/'

const isAuthenticated = async(to, from, next) => {
    if (!window.keycloak || !localStorage.getItem('vue-token')) {
        await login({ onLoad: 'login-required', pkceMethod: 'S256', locale: getLanguage() })
    }
    const store = useUserStore()
    await store.authenticated(window.keycloak)
    next()
}

const getWebtargetConfigByPostRegistrationKey = async query => {
    if (typeof query.prk === 'string') {
        query.prk = query.prk.toUpperCase()
    }

    if (
        query.prk !== null &&
        typeof query.prk != 'undefined' &&
        query.prk.length
    ) {
        try {
            const result = await getWebTarget(query.prk)
            if (result.data && result.data.url) {
                return result.data
            }
            return { key: null, correlationid: result.headers.correlationid }
        } catch (err) {
            return {
                key: null,
                correlationid: err.response.headers.correlationid
            }
        }
    } else {
        return { key: null }
    }
}

const getPostRegistrationKey = async query => {
    if (typeof query.prk === 'string') {
        query.prk = query.prk.toUpperCase()
    }

    if (
        query.prk !== null &&
        typeof query.prk !== 'undefined' &&
        query.prk.length
    ) {
        try {
            const result = await getWebTarget(query.prk)
            if (result.data && result.data.url) {
                return query.prk
            }
        } catch (err) {
            return null
        }
    }
    return null
}

const getPostRegistrationKeyForLogin = async query => {
    if (typeof query.prk === 'string') {
        query.prk = query.prk.toUpperCase()
    }
    if (query.prk !== null && typeof query.prk != 'undefined') {
        query.prk = !query.prk.length ? 'MC' : query.prk
        try {
            const result = await getWebTarget(query.prk)
            if (result.data && result.data.url) {
                return query.prk
            }
        } catch (err) {
            return null
        }
    }
    return null
}

const getCrefonummer = query => {
    if (
        typeof query.crefonummer === 'string' &&
        query.crefonummer.length &&
        new RegExp('^[0-9]+$').test(query.crefonummer)
    ) {
        return query.crefonummer
    }

    return null
}

const getMitgliedskennung = query => {
    if (
        typeof query.mitgliedskennung === 'string' &&
        query.mitgliedskennung.length &&
        new RegExp('^[0-9]+$').test(query.mitgliedskennung)
    ) {
        return query.mitgliedskennung
    }

    if (
        typeof query.memberUserId === 'string' &&
        query.memberUserId.length &&
        new RegExp('^[0-9]+$').test(query.memberUserId)
    ) {
        return query.memberUserId
    }

    return null
}

const hydrateProps = (route, props) => {
    Object.assign(route.meta, { props })
}

const generateRegisterProps = (initMemberRegistration = false) => {
    return route => {
        const registerProps = {
            prk: route.meta.props?.webtargetConfig?.key,
            prt: route.query?.prt,
            crefonummer: getCrefonummer(route.query),
            mitgliedskennung: getMitgliedskennung(route.query),
            hideSmartSignup:
                !!route.query.showSmartSignup &&
                route.query.showSmartSignup === '0',
            showSmartSignup:
                !!route.query.showSmartSignup &&
                route.query.showSmartSignup === '1',
            showMemberChoice:
            route.meta.props?.webtargetConfig?.themeOpts?.registration
                ?.showMemberChoice,
            webtargetConfig: route.meta.props?.webtargetConfig,
            initMemberRegistration: initMemberRegistration
        }

        if (initMemberRegistration === true) {
            registerProps.initMemberRegistration = true
            registerProps.hideSmartSignup = true
            registerProps.showSmartSignup = false
        }

        return registerProps
    }
}

const resolveWebtargetConfig = async(to, from, next) => {
    const webtargetConfig = await getWebtargetConfigByPostRegistrationKey(
        to.query
    )
    hydrateProps(to, { webtargetConfig })
    next()
}

const resolvePostRegistrationKey = async(to, from, next) => {
    const prk = await getPostRegistrationKey(to.query)
    hydrateProps(to, { prk })
    next()
}

const routes = [
    {
        path: '/',
        name: 'home',
        beforeEnter: async(to, from, next) => {
            const store = useUserStore()
            /**
             * When a user enters via root path, check if the user has an active SSO session.
             * If so, redirect to the account overview page. Otherwise to the registration form.
             */
            if (window.keycloak && localStorage.getItem('vue-token')) {
                next({ name: 'profile' })
            } else {
                await login(
                    {
                        onLoad: 'check-sso',
                        pkceMethod: 'S256',
                        locale: getLanguage(),
                        silentCheckSsoRedirectUri:
                            window.location.origin + APP_BASE +'/silent-check-sso.html'
                    },
                    next,
                    {
                        onLoggedIn: 'profile',
                        onLoggedOut: 'register'
                    }
                )
            }
            await store.authenticated(window.keycloak)
        }
    },
    {
        path: '/konto',
        name: 'profile',
        beforeEnter: isAuthenticated,
        component: Person
    },
    {
        path: '/konto/danke',
        name: 'patch-person-success',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/person/success')
    },
    {
        path: '/konto/email-aendern',
        name: 'change-mail-address',
        beforeEnter: isAuthenticated,
        component: () =>
            import('@/views/selfcare/person/change-mail-address/index')
    },
    {
        path: '/konto/email-aendern/danke',
        name: 'change-mail-address-success',
        beforeEnter: isAuthenticated,
        component: () =>
            import('@/views/selfcare/person/change-mail-address/success')
    },
    {
        path: '/konto/email-aenderung-bestaetigen',
        name: 'change-mail-address-optin',
        component: () =>
            import('@/views/selfcare/person/change-mail-address/optin'),
        props: route => ({
            optinId: route.query.optinId
        })
    },
    {
        path: '/konto/loeschen',
        name: 'request-account-deletion',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/person/delete-account/index')
    },
    {
        path: '/konto/loeschen/danke',
        name: 'request-account-deletion-success',
        beforeEnter: isAuthenticated,
        component: () =>
            import('@/views/selfcare/person/delete-account/success')
    },
    {
        path: '/konto/loeschen/bestaetigen',
        name: 'confirm-account-deletion',
        component: () =>
            import('@/views/selfcare/person/delete-account/confirm'),
        props: route => ({
            optOutToken: route.query.optoutId
        })
    },
    {
        path: '/konto/passwort-aendern',
        name: 'change-password',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/person/change-password/index')
    },
    {
        path: '/konto/passwort-aendern/danke',
        name: 'change-password-success',
        beforeEnter: isAuthenticated,
        component: () =>
            import('@/views/selfcare/person/change-password/success')
    },
    {
        path: '/sicherheit',
        name: 'security',
        beforeEnter: isAuthenticated,
        component: Security
    },
    {
        path: '/unternehmen',
        name: 'company',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/company/index')
    },
    {
        path: '/unternehmen/danke',
        name: 'patch-person-company-success',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/company/success')
    },
    {
        path: '/mitgliedschaft',
        name: 'membership',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/membership/index')
    },
    {
        path: '/mitgliedschaft/danke',
        name: 'register-membership-success',
        beforeEnter: isAuthenticated,
        component: () => import('@/views/selfcare/membership/success')
    },
    {
        path: '/registrieren',
        name: 'register',
        component: () => import('@/views/register/index'),
        beforeEnter: resolveWebtargetConfig,
        props: generateRegisterProps(false)
    },
    {
        path: '/registrieren/mitglied',
        name: 'register-members',
        component: () => import('@/views/register/index'),
        beforeEnter: resolveWebtargetConfig,
        props: generateRegisterProps(true)
    },
    {
        path: '/registrieren/danke',
        name: 'register-success',
        component: () => import('@/views/register/success'),
        beforeEnter: resolvePostRegistrationKey,
        props: route => ({
            prk: route.meta.props.prk,
            prt: route.query.prt
        })
    },
    {
        path: '/registrierung-bestaetigen',
        name: 'register-optin',
        component: () => import('@/views/register/optin'),
        beforeEnter: resolvePostRegistrationKey,
        props: route => ({
            prk: route.meta.props.prk,
            prt: route.query.prt,
            optinId: route.query.optinId
        })
    },
    {
        path: '/passwort-vergessen',
        name: 'password-forgotten',
        component: () => import('@/views/password-forgotten/index'),
        beforeEnter: resolvePostRegistrationKey,
        props: route => ({
            prk: route.meta.props.prk,
            prt: route.query.prt
        })
    },
    {
        path: '/passwort-vergessen/danke',
        name: 'password-forgotten-success',
        component: () => import('@/views/password-forgotten/success')
    },
    {
        path: '/passwort-zuruecksetzen',
        name: 'password-reset',
        component: () => import('@/views/password-reset/index'),
        beforeEnter: resolvePostRegistrationKey,
        props: route => ({
            prk: route.meta.props.prk,
            prt: route.query.prt,
            passwordResetToken: route.query.passwordResetToken
        })
    },
    {
        path: '/passwort-zuruecksetzen/danke',
        name: 'password-reset-success',
        component: () => import('@/views/password-reset/success'),
        beforeEnter: resolvePostRegistrationKey,
        props: route => ({
            prk: route.meta.props.prk,
            prt: route.query.prt
        })
    },
    {
        path: '/login',
        name: 'login',
        beforeEnter: async(to, from, next) => {
            const store = useUserStore()
            const login = async() => {
                const prk = await getPostRegistrationKeyForLogin(to.query)
                const prt = to.query.prt
                const isNewUser = to.query.newUser

                let redirectUri = `${location.protocol}//${location.host}${APP_BASE}konto`
                if (isNewUser) {
                    redirectUri += '?newUser'
                }

                if (prk !== null) {
                    let webTarget = await getWebTarget(prk)
                    if (webTarget.status === 200 && webTarget.data.url) {
                        redirectUri = webTarget.data.url

                        if (isNewUser) {
                            if (redirectUri.indexOf('?') > 0) {
                                redirectUri += '&newUser'
                            } else {
                                redirectUri += '?newUser'
                            }
                        }

                        if (prt) {
                            if (redirectUri.indexOf('?') > 0) {
                                redirectUri += '&prt=' + encodeURIComponent(prt)
                            } else {
                                redirectUri += '?prt=' + encodeURIComponent(prt)
                            }
                        }

                        if (redirectUri.indexOf('?') > 0) {
                            redirectUri += '&prk=' + prk
                        } else {
                            redirectUri += '?prk=' + prk
                        }
                    }
                }

                location.assign(redirectUri)
            }

            await login()
            await store.authenticated(window.keycloak)
            next()
        }
    },
    {
        path: '/logout',
        name: 'logout',
        beforeEnter: async() => {
            const store = useUserStore()
            await logout()
            await store.logout()
        }
    }
]
const router = createRouter({
    history: createWebHistory(APP_BASE),
    scrollBehavior: to => {
        if (!to.hash) {
            window.scroll(0, 0)
        }
    },
    routes
})

router.beforeEach((to, from, next) => {
    if (from.query.client_id && !to.query.client_id) {
        to.query.client_id = from.query.client_id
    }
    if (from.query.prk && !to.query.prk) {
        to.query.prk = from.query.prk
    }

    next()
})

export { routes }
export default {
    install(app) {
        router.install(app)
        router.afterEach(to => {
            let lang = app.$i18n?.locale || 'de_DE'
            app.config.globalProperties.$wt.page(to.name, {}, lang)
            app.config.globalProperties.$wt.trackPage()
        })
    }
}
